/**
 * adminSections — the `/admin` console's section tree, shared between the
 * Sidenav (which renders it as the "admin" level) and the Admin page (which
 * maps each section id to its lazy component). Kept component-free so the
 * Sidenav doesn't pull in the admin section chunks.
 */

export interface AdminNavLeaf {
  id: string;
  label: string;
  desc?: string;
  /** External route instead of an in-page `?section=` (e.g. the billing console). */
  href?: string;
}

export interface AdminNavGroup {
  id: string;
  label: string;
  leaves: AdminNavLeaf[];
}

export const ADMIN_SECTIONS: AdminNavGroup[] = [
  {
    id: 'system', label: 'System',
    leaves: [
      { id: 'overview', label: 'Overview', desc: 'Numbers at a glance' },
      { id: 'system-health', label: 'System health', desc: 'Subsystem checks' },
      { id: 'helper-calls', label: 'Helper logs', desc: 'voxcore-helper calls' },
    ],
  },
  {
    id: 'tenants', label: 'Tenants',
    leaves: [
      { id: 'customers', label: 'Customers', desc: 'CRUD + limits + keys' },
      { id: 'bots', label: 'Bots', desc: 'All bots, all customers' },
      { id: 'resellers', label: 'Resellers', desc: 'Reseller orgs + linking' },
      { id: 'phone-numbers', label: 'Phone numbers', desc: 'Customer-scoped DIDs' },
      { id: 'sip-trunks', label: 'SIP trunks', desc: 'Carriers / PBXes allowed into the SIP edge, outbound routes' },
      { id: 'user-roles', label: 'User roles', desc: 'Roles per user' },
      { id: 'personal-keys', label: 'Personal API keys', desc: 'Admin-rights keys for tools, per admin' },
    ],
  },
  {
    id: 'money', label: 'Money',
    leaves: [
      { id: 'billing-console', label: 'Billing console', desc: 'Stripe catalog, customers, discounts, invoices', href: '/admin/billing' },
      { id: 'platform-billing', label: 'Platform billing', desc: 'Cost knobs + LLM prices' },
      { id: 'non-stripe', label: 'Non-Stripe defaults', desc: 'Trial limits & defaults' },
    ],
  },
  {
    id: 'tools', label: 'Tools',
    leaves: [
      { id: 'llm-defaults', label: 'LLM defaults', desc: 'Fleet default model, fallback, temperature per model' },
      { id: 'endpoints', label: 'Endpoints', desc: 'OpenAI-compatible LLM endpoints + STT endpoints, discovery, test' },
      { id: 'voices', label: 'Voices', desc: 'TTS preset catalog' },
      { id: 'plugins', label: 'Integration plugins', desc: 'Author external integration plugins (agentic editor)' },
      { id: 'maintenance', label: 'Maintenance', desc: 'One-shot ops & syncs' },
      { id: 'default-bot', label: 'Default bot', desc: 'Public main bot config' },
    ],
  },
  {
    id: 'quality', label: 'Quality',
    leaves: [
      { id: 'harness-eval', label: 'Harness eval', desc: 'Grade the graph-generation LLM end-to-end' },
      { id: 'conv-traces', label: 'Conversation traces', desc: 'Replay the workflow-bot scenario tests and read every prompt' },
    ],
  },
];

export const ADMIN_LEAVES: AdminNavLeaf[] = ADMIN_SECTIONS.flatMap((g) => g.leaves);
export const ADMIN_DEFAULT_SECTION = 'overview';

const KNOWN_SECTIONS = new Set(ADMIN_LEAVES.map((l) => l.id));

/** Current section id from a `?section=` query string (defaults to overview).
 *  Kept for back-compat active-state on old `/admin?section=` links. */
export function adminSectionFromSearch(search: string): string {
  const sp = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  return sp.get('section') || ADMIN_DEFAULT_SECTION;
}

/** Resolve the active admin route from the URL. The canonical form is the path
 *  `/admin/<section>[/<sub>]` (e.g. `/admin/plugins/pl_x`); the legacy
 *  `?section=` query is still honored as a fallback so old links keep working.
 *  Unknown/missing → overview. */
export function adminRoute(pathname: string, search: string): { section: string; sub?: string } {
  const m = pathname.match(/^\/admin(?:\/([^/]+)(?:\/([^/]+))?)?\/?$/);
  if (m && m[1] && KNOWN_SECTIONS.has(m[1])) {
    return { section: m[1], sub: m[2] ? decodeURIComponent(m[2]) : undefined };
  }
  const sp = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const q = sp.get('section');
  if (q && KNOWN_SECTIONS.has(q)) return { section: q };
  return { section: ADMIN_DEFAULT_SECTION };
}

/** Href for a leaf — its external route, or the canonical `/admin/<id>` path. */
export function adminSectionHref(leaf: AdminNavLeaf): string {
  return leaf.href ?? `/admin/${leaf.id}`;
}

/** Deep-link into a section sub-view, e.g. a specific plugin: `/admin/plugins/pl_x`. */
export function adminSubHref(sectionId: string, sub: string): string {
  return `/admin/${sectionId}/${encodeURIComponent(sub)}`;
}
